import { useEffect, useState } from 'react';
import { INCIDENTI, GRAVITA_COL } from './data/incidenti';
import type { Destinatario, Richiesta } from './data/incidenti';
import { componiBriefing, chiediAssistenza } from './assistente';

/**
 * `POST /api/assistenza` (vedi assistente.ts) non esiste ancora su nessun
 * backend — nessun servizio sotto `services/` lo espone. Finché non viene
 * deciso e implementato il gateway reale, il bottone resta visibilmente
 * disattivato invece di fallire in interfaccia con "Assistente non
 * raggiungibile". "Copia il briefing" resta invece pienamente funzionante
 * (puro client-side): è la via nel frattempo.
 */
// `as boolean` (non un'annotazione di tipo): evita che TS tratti il valore
// come il literal `false`, che farebbe segnalare a eslint come "sempre
// falsi/veri" tutti i condizionali sotto che dipendono da questo flag.
const ASSISTENTE_DISPONIBILE = false as boolean;

export function RisoluzioneAssistita() {
  const [incId, setIncId] = useState(INCIDENTI[0]!.id);
  const [dest, setDest] = useState<Destinatario>('code');
  const [richiesta, setRichiesta] = useState<Richiesta>('patch');
  const [briefing, setBriefing] = useState<string | null>(null);   // null = derivato
  const [risposta, setRisposta] = useState('');
  const [errore, setErrore] = useState('');
  const [attesa, setAttesa] = useState(false);
  const [copiato, setCopiato] = useState(false);

  const inc = INCIDENTI.find((i) => i.id === incId) ?? INCIDENTI[0]!;
  const testo = briefing ?? componiBriefing(inc, dest, richiesta);

  // Cambiare incidente o destinatario ricompone il briefing: se l'utente lo ha
  // modificato a mano, la modifica va persa DI PROPOSITO — un briefing metà
  // vecchio e metà nuovo è la cosa peggiore da mandare a un assistente.
  useEffect(() => { setBriefing(null); setRisposta(''); setErrore(''); }, [incId, dest, richiesta]);
  useEffect(() => {
    if (!copiato) return;
    const t = setTimeout(() => setCopiato(false), 2200);
    return () => clearTimeout(t);
  }, [copiato]);

  async function invia() {
    if (attesa || !ASSISTENTE_DISPONIBILE) return;
    setAttesa(true); setErrore(''); setRisposta('');
    try {
      setRisposta(await chiediAssistenza(testo));
    } catch (e) {
      setErrore(e instanceof Error ? e.message : String(e));
    } finally {
      setAttesa(false);
    }
  }

  return (
    <>
      <h1>Risoluzione assistita</h1>
      <p className="lede">
        Scegli un problema aperto: il cruscotto compone il briefing tecnico con lo stack, i vincoli della
        piattaforma e quello che hai già verificato, poi lo gira all'assistente o te lo consegna pronto da
        incollare in Claude Code.
      </p>

      <div className="uc-grafo-riga">
        <div className="uc-card" style={{ flex: '1 1 210px', minWidth: 196 }}>
          <h2 style={{ marginBottom: 3 }}>Problemi aperti</h2>
          <p style={{ fontSize: 12, color: 'var(--uc-ink-3)', margin: '0 0 11px' }}>Dalla coda del cruscotto.</p>
          <div className="uc-inc-lista">
            {INCIDENTI.map((i) => (
              <button key={i.id} type="button" className="uc-inc" aria-pressed={i.id === incId} onClick={() => setIncId(i.id)}>
                <span className="riga">
                  <span style={{ background: GRAVITA_COL[i.gravita] }} aria-hidden="true" />
                  <span className="titolo">{i.titolo}</span>
                </span>
                <span className="strato">{i.strato}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="uc-card" style={{ flex: '2 1 340px', minWidth: 0, padding: '18px 19px' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
            <h2 style={{ fontSize: 16, margin: 0 }}>{inc.titolo}</h2>
            <span className="mono" style={{ fontSize: 11.5, color: 'var(--uc-ink-3)', border: '1px solid #e2ded5', background: 'var(--uc-carta-2)', borderRadius: 3, padding: '2px 7px' }}>
              {inc.strato}
            </span>
          </div>
          <p style={{ fontSize: 13.5, lineHeight: 1.55, color: 'var(--uc-ink-2)', margin: '8px 0 0' }}>{inc.sintomo}</p>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginTop: 16 }}>
            <label style={{ flex: '1 1 180px', minWidth: 0, font: '600 12.5px inherit', color: 'var(--uc-ink-2)' }}>
              Destinatario
              <select className="uc-sel" style={{ marginTop: 6 }} value={dest} onChange={(e) => setDest(e.target.value as Destinatario)}>
                <option value="code">Claude Code — ha accesso al repository</option>
                <option value="design">Claude Design — interfaccia e copy</option>
                <option value="ticket">Ticket interno — linguaggio non tecnico</option>
              </select>
            </label>
            <label style={{ flex: '1 1 180px', minWidth: 0, font: '600 12.5px inherit', color: 'var(--uc-ink-2)' }}>
              Cosa chiedo
              <select className="uc-sel" style={{ marginTop: 6 }} value={richiesta} onChange={(e) => setRichiesta(e.target.value as Richiesta)}>
                <option value="diagnosi">Solo diagnosi, nessun codice</option>
                <option value="patch">Diagnosi e patch minima</option>
                <option value="staging">Patch e sequenza di verifica in staging</option>
              </select>
            </label>
          </div>

          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, margin: '18px 0 7px', flexWrap: 'wrap' }}>
            <span style={{ font: '700 12.5px inherit', color: 'var(--uc-ink-2)' }}>Briefing tecnico</span>
            <span style={{ fontSize: 12, color: 'var(--uc-ink-3)' }}>Modificabile. Si ricompone se cambi le scelte sopra.</span>
          </div>
          <textarea className="uc-brief" rows={14} value={testo} onChange={(e) => setBriefing(e.target.value)} />

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 9, marginTop: 14, alignItems: 'center' }}>
            <button
              type="button"
              className="uc-btn ia"
              onClick={invia}
              disabled={attesa || !ASSISTENTE_DISPONIBILE}
              title={ASSISTENTE_DISPONIBILE ? undefined : 'In arrivo — il gateway di inferenza non è ancora collegato.'}
            >
              {!ASSISTENTE_DISPONIBILE ? "Chiedi all'assistente — in arrivo" : attesa ? 'Analisi in corso…' : "Chiedi all'assistente"}
            </button>
            <button
              type="button"
              className="uc-btn secondario"
              style={{ minHeight: 42, padding: '0 15px', fontSize: 14 }}
              onClick={() => { void navigator.clipboard?.writeText(testo); setCopiato(true); }}
            >
              {copiato ? 'Copiato' : 'Copia il briefing'}
            </button>
            {/* Dichiarazione obbligatoria: il briefing esce dall'infrastruttura. */}
            <span style={{ fontSize: 12.5, color: 'var(--uc-ink-3)' }}>
              {ASSISTENTE_DISPONIBILE
                ? 'Il briefing non contiene dati personali né contenuto degli atti.'
                : "L'assistente automatico non è ancora collegato a un backend reale: usa «Copia il briefing» e incollalo in Claude Code."}
            </span>
          </div>
        </div>

        <div className="uc-ia-pannello" style={{ flex: '2 1 320px', minWidth: 0, alignSelf: 'stretch' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
            <h2>Risposta dell'assistente</h2>
            <span className="uc-ia-tag">{ASSISTENTE_DISPONIBILE ? 'modello on-premise' : 'in arrivo'}</span>
          </div>

          {!ASSISTENTE_DISPONIBILE && (
            <p style={{ margin: '10px 0 0' }}>
              Il gateway di inferenza per questa funzione non è ancora collegato. Componi comunque il
              briefing a sinistra e usa «Copia il briefing» per portarlo dove serve nel frattempo.
            </p>
          )}

          {ASSISTENTE_DISPONIBILE && !risposta && !errore && !attesa && (
            <>
              <p style={{ margin: '10px 0 0' }}>
                Nessuna richiesta inviata. La risposta arriva strutturata in cinque punti: diagnosi più
                probabile, ipotesi alternative, cosa fare, come verificare, rischi.
              </p>
              <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid rgba(255,255,255,.16)', font: '12.5px/1.75 inherit', color: 'rgba(255,255,255,.72)' }}>
                <div>· nessun dato personale nel briefing</div>
                <div>· la richiesta resta tracciata nel registro eventi</div>
                <div>· nessuna patch viene applicata automaticamente</div>
              </div>
            </>
          )}

          {attesa && <p style={{ margin: '14px 0 0', color: '#cfcbf7' }}>Sto analizzando il problema…</p>}

          {errore && (
            <div className="uc-ia-errore" role="alert">
              <strong>Richiesta non completata</strong>
              <p style={{ fontSize: 12.5, margin: '4px 0 0', color: 'rgba(255,255,255,.82)' }}>{errore}</p>
              <p style={{ fontSize: 12.5, margin: '8px 0 0', color: 'rgba(255,255,255,.82)' }}>
                Il briefing resta valido: copialo e incollalo in Claude Code.
              </p>
            </div>
          )}

          {risposta && (
            <div className="uc-ia-out">
              <pre>{risposta}</pre>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
